'use client'

import type { ReactNode } from 'react'
import {
  BlockStack,
  Box,
  Button,
  DatePicker,
  InlineStack,
  Popover,
  Text,
  TextField,
} from '@shopify/polaris'
import { CalendarIcon } from '@shopify/polaris-icons'
import { useCallback, useEffect, useState } from 'react'
import { businessIsoDateToPickerDate, toCalendarDateStringInTimeZone } from '@/lib/timezone'
import { ReportingDatePresetButtons } from '@/components/reporting/ReportingDatePresetButtons'
import type { ReportingDatePresetId } from '@/lib/dashboard/reportingDatePresets'
import { formatCompactDateRangeForDashboard } from '@/lib/dashboard/formatCompactDateRange'

type Props = {
  dateFrom: string
  dateTo: string
  /** Shop IANA zone from MerchantProfile (e.g. Asia/Karachi = PKT). Must match dashboard API rollup. */
  timeZone: string
  onRangeComplete: (from: string, to: string) => void
  onSelectPreset: (preset: ReportingDatePresetId) => void
  onReload: () => void
  reloadDisabled?: boolean
  disabled?: boolean
  trailingSlot?: ReactNode
}

function clampOrder(a: string, b: string): { from: string; to: string } {
  return a <= b ? { from: a, to: b } : { from: b, to: a }
}

export function DashboardPeriodRangeControl({
  dateFrom,
  dateTo,
  timeZone,
  onRangeComplete,
  onSelectPreset,
  onReload,
  reloadDisabled,
  disabled,
  trailingSlot,
}: Props) {
  const [popoverActive, setPopoverActive] = useState(false)
  /** Draft range while the popover is open; committed only via Load (or synced from props when closed). */
  const [draftFrom, setDraftFrom] = useState(dateFrom)
  const [draftTo, setDraftTo] = useState(dateTo)
  const [viewYear, setViewYear] = useState(() => businessIsoDateToPickerDate(dateFrom).getUTCFullYear())
  const [viewMonth0, setViewMonth0] = useState(() => businessIsoDateToPickerDate(dateFrom).getUTCMonth())

  useEffect(() => {
    const a = businessIsoDateToPickerDate(dateFrom)
    setViewYear(a.getUTCFullYear())
    setViewMonth0(a.getUTCMonth())
  }, [dateFrom])

  const [selectedDates, setSelectedDates] = useState<{ start: Date; end: Date }>(() => ({
    start: businessIsoDateToPickerDate(dateFrom),
    end: businessIsoDateToPickerDate(dateTo),
  }))

  useEffect(() => {
    setSelectedDates({
      start: businessIsoDateToPickerDate(dateFrom),
      end: businessIsoDateToPickerDate(dateTo),
    })
  }, [dateFrom, dateTo])

  useEffect(() => {
    if (!popoverActive) {
      setDraftFrom(dateFrom)
      setDraftTo(dateTo)
    }
  }, [dateFrom, dateTo, popoverActive])

  /** Update draft + picker highlight from calendar; does not apply to dashboard until Load. */
  const setDraftRangeFromPicker = useCallback((start: Date, end: Date) => {
    const a = toCalendarDateStringInTimeZone(start, timeZone)
    const b = toCalendarDateStringInTimeZone(end, timeZone)
    if (!a || !b) return
    const { from, to } = clampOrder(a, b)
    setSelectedDates({ start, end })
    setDraftFrom(from)
    setDraftTo(to)
  }, [timeZone])

  const onCalendarChange = useCallback(
    (range: { start?: Date; end?: Date }) => {
      if (range.start && range.end) {
        setDraftRangeFromPicker(range.start, range.end)
        return
      }
      if (range.start) {
        setSelectedDates({ start: range.start, end: range.start })
      }
    },
    [setDraftRangeFromPicker]
  )

  const handleLoadClick = useCallback(() => {
    onRangeComplete(draftFrom, draftTo)
    onReload()
    setPopoverActive(false)
  }, [draftFrom, draftTo, onRangeComplete, onReload])

  const triggerLabel = formatCompactDateRangeForDashboard(dateFrom, dateTo, timeZone)

  const popoverDisabled = Boolean(disabled)

  const popoverContent = (
    <Box padding="300" maxWidth="340px">
      <BlockStack gap="200">
        <ReportingDatePresetButtons
          onSelect={(id) => {
            onSelectPreset(id)
            setPopoverActive(false)
          }}
          disabled={popoverDisabled}
        />
        <div style={{ ...(popoverDisabled ? { pointerEvents: 'none' as const, opacity: 0.55 } : {}) }}>
          <DatePicker
            month={viewMonth0}
            year={viewYear}
            onMonthChange={(m, y) => {
              setViewMonth0(m)
              setViewYear(y)
            }}
            selected={selectedDates}
            onChange={onCalendarChange}
            allowRange
          />
        </div>
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <Box minWidth="0" maxWidth="140px">
            <TextField
              label="From"
              labelHidden
              type="date"
              value={draftFrom}
              onChange={setDraftFrom}
              autoComplete="off"
              disabled={popoverDisabled}
            />
          </Box>
          <Text as="span" variant="bodySm" tone="subdued">
            –
          </Text>
          <Box minWidth="0" maxWidth="140px">
            <TextField
              label="To"
              labelHidden
              type="date"
              value={draftTo}
              onChange={setDraftTo}
              autoComplete="off"
              disabled={popoverDisabled}
            />
          </Box>
        </InlineStack>
        <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
          <Button variant="tertiary" onClick={() => setPopoverActive(false)} disabled={popoverDisabled}>
            Close
          </Button>
          <Button onClick={handleLoadClick} disabled={reloadDisabled || popoverDisabled} variant="primary" size="slim">
            Load
          </Button>
        </InlineStack>
      </BlockStack>
    </Box>
  )

  return (
    <Box padding="300" background="bg-surface" borderWidth="025" borderColor="border" borderRadius="300">
      <InlineStack align="space-between" blockAlign="center" wrap gap="300">
        <BlockStack gap="050">
          <Text as="p" variant="headingMd">
            Dashboard period
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Store timezone: {timeZone}
            {timeZone === 'Asia/Karachi' ? ' (PKT)' : null}
          </Text>
        </BlockStack>
        <InlineStack gap="200" blockAlign="center" wrap>
          {trailingSlot}
          <Popover
            active={popoverActive}
            activator={
              <Button
                icon={CalendarIcon}
                onClick={() => setPopoverActive((a) => !a)}
                disabled={popoverDisabled}
                disclosure={popoverActive ? 'up' : 'down'}
              >
                {triggerLabel}
              </Button>
            }
            autofocusTarget="none"
            /** DatePicker uses nested overlays; without this, the first range click often dismisses the popover. */
            preventCloseOnChildOverlayClick
            onClose={() => setPopoverActive(false)}
            preferredAlignment="right"
            preferredPosition="below"
          >
            {popoverContent}
          </Popover>
        </InlineStack>
      </InlineStack>
    </Box>
  )
}
